import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { useCart } from '../hooks/useCart';
import { useOnDismiss } from '../hooks/useOnDismiss';
import { listFoodMenu } from '../utils/food';
import {
    formatHotelNights,
    formatHotelRange,
    getHotelRoomTypesList,
} from '../utils/hotels';
import { convertAmps } from '../utils/string';
import { formatTicketDate, formatTicketVenue } from '../utils/tickets';
import { translate } from '../utils/wp.js';

export function Cart({visible, onClose}) {
    const cart = useRef(null);

    useOnDismiss(cart, visible, onClose);

    const prices = useCart();

    if (!visible) {
        return null;
    }

    return (
        <div className="fixed z-[100] inset-0 bg-black/30">
            <div
                className="bg-white p-4 rounded absolute top-20 left-4 right-4 md:top-24"
                ref={cart}
            >
                <div className="text-lg font-semibold flex items-center justify-between">
                    {translate('summary')}
                    <button
                        className="icon-plus rotate-45 text-[20px]"
                        onClick={onClose}
                    />
                </div>
                <div className="w-full flex items-center justify-between pt-4 pb-3 text-sm font-medium">
                    <div>{translate('price_per_person')}</div>
                    <div className="ml-auto mr-8">{prices.quantity}&nbsp;x</div>
                    <div
                        className="whitespace-nowrap"
                        translate="no"
                    >
                        {prices.ppt} SEK
                    </div>
                </div>
                {prices.addons.single > 0 &&
                    <div className="w-full flex items-center justify-between pt-4 pb-3 text-sm font-medium">
                        <div>{translate('single_room_addon')}</div>
                        <div
                            className="whitespace-nowrap"
                            translate="no"
                        >
                            {prices.addons.single} SEK
                        </div>
                    </div>
                }
                <div className="w-full flex items-center justify-between pt-4 pb-3 border-y border-black/10 text-sm font-bold">
                    <div>{translate('total')}</div>
                    <div
                        className="whitespace-nowrap"
                        translate="no"
                    >
                        {prices.price} SEK
                    </div>
                </div>

                <Event />
                <Hotel />
            </div>
        </div>
    );
}

function Event() {
    const product = useSelector(state => state.cart.product);
    const tickets = useSelector(state => state.cart.confirmed_tickets);
    const ticket = useSelector(
        state => state.cart.confirmed_tickets.length > 0
            ? state.cart.confirmed_tickets[0]
            : null,
    );
    const food = useSelector(state => state.cart.confirmed_food);

    return (
        <div className="flex items-center gap-4 mt-4">
            <div className="relative rounded overflow-hidden aspect-square basis-20">
                {product &&
                    <img
                        className="absolute block inset-0 h-full w-full object-cover object-center"
                        src={product.image.thumbnail}
                        alt={convertAmps(product.title)}
                        width="80"
                        height="80"
                    />
                }

                {!product &&
                    <div className="absolute inset-0 h-full w-full object-cover bg-disabled flex items-center justify-center">
                        <i className="icon-ticket opacity-20 text-[24px]"></i>
                    </div>
                }
            </div>
            <div className="flex-1 text-xs font-medium text-left">
                {product &&
                    <>
                        <div className="mb-0.5 font-bold text-sm">
                            {convertAmps(product.title)}
                        </div>
                        <div className="opacity-50">
                            {tickets.length} x {translate(tickets[0].name)}
                        </div>
                        <div className="opacity-50">
                            {formatTicketDate(ticket)}
                        </div>
                        <div className="opacity-50">
                            {formatTicketVenue(ticket)}
                        </div>
                        {food &&
                            <div className="opacity-50">
                                {listFoodMenu(food)}
                            </div>
                        }
                    </>
                }

                {!product &&
                    <p className="opacity-50">
                        {translate('no_added_tickets_please_add')}
                    </p>
                }
            </div>
        </div>
    );
}

function Hotel() {
    const hotel = useSelector(state => state.cart.confirmed_hotel);
    const range = useSelector(
        state => state.cart.confirmed_hotel
            ? state.cart.confirmed_hotel.ranges[state.cart.selected_hotel_nights][state.cart.selected_hotel_range]
            : null,
    );
    const selectedRoomType = useSelector(state => state.cart.selected_hotel_room_type);

    const tickets = useSelector(state => state.cart.confirmed_tickets);

    return (
        <div className="flex items-center gap-4 mt-4">
            <div className="relative rounded overflow-hidden aspect-square basis-20">
                {hotel &&
                    <img
                        className="absolute inset-0 h-full w-full object-cover object-center"
                        src={hotel.image_meta.thumbnail}
                        alt={convertAmps(hotel.name)}
                        width="80"
                        height="80"
                    />
                }

                {!hotel &&
                    <div className="absolute inset-0 h-full w-full object-cover bg-disabled flex items-center justify-center">
                        <i className="icon-hotel opacity-20 text-[24px]"></i>
                    </div>
                }
            </div>
            <div className="flex-1 text-xs font-medium text-left">
                {hotel &&
                    <>
                        <div className="mb-0.5 font-bold text-sm">
                            {convertAmps(hotel.name)}
                        </div>
                        <div className="opacity-50">
                            {
                                [
                                    ...getHotelRoomTypesList(
                                        tickets.length,
                                        hotel.room_types,
                                        selectedRoomType,
                                    ),
                                    hotel.offers.breakfast === 'yes'
                                        ? translate('incl_breakfast')
                                        : null,
                                ].filter(Boolean).join(', ')}
                        </div>
                        <div className="opacity-50">{formatHotelNights(range.range)}</div>
                        <div className="opacity-50">
                            {formatHotelRange(range)}
                        </div>
                    </>
                }

                {!hotel &&
                    <p className="opacity-50">
                        {translate('no_selected_hotel_please_choose')}
                    </p>
                }
            </div>
        </div>
    );
}
